<template>
    <div class="help-article" :class="{'is-open': isOpen, 'is-page': page}">
        <component :is="page ? 'h3' : 'a'" class="help-question" @click="toggle" :aria-expanded="page ? null : String(isOpen)">
            <i v-if="!page" class="far fa-fw mr-3 toggle-icon" :class="isOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            <span>{{ article.q }}</span>
        </component>

        <div v-if="isOpen" class="help-answer">
            <div class="help-body content" v-html="article.a"></div>

            <div class="help-actions" v-if="buttons.length">
                <template v-for="action in buttons">
                    <b-button v-if="action.to" :key="action.label" tag="router-link" :to="action.to"
                              type="is-primary" size="is-small" outlined>
                        <i class="fas fa-fw mr-2" :class="action.icon"></i>{{ action.label }}
                    </b-button>
                    <b-button v-else :key="action.label" tag="a" :href="action.href"
                              :target="action.external ? '_blank' : null" :rel="action.external ? 'noopener' : null"
                              type="is-primary" size="is-small" outlined>
                        <i class="fas fa-fw mr-2" :class="action.icon"></i>{{ action.label }}
                    </b-button>
                </template>
                <b-button v-if="article.contact && showContact" tag="router-link" :to="{name: 'support', params: {view: 'new'}}"
                          type="is-primary" size="is-small">
                    <i class="fas fa-fw fa-paper-plane mr-2"></i>Send Us a Message
                </b-button>
            </div>

            <div class="help-meta" v-if="!page && linkable">
                <router-link :to="{name: 'support', params: {view: 'help', item: article.id}}">
                    <i class="far fa-link mr-1"></i>Link to This Answer
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
    // One Help Center question and answer (constants/helpCenter). Collapsed
    // rows toggle open in place; `page` renders it open as the article page.
    export default {
        name: "HelpArticle",
        props: {
            article: {type: Object, required: true},
            open: {type: Boolean, default: false},
            page: {type: Boolean, default: false},
            // false inside the new-ticket form, which already is the contact path
            showContact: {type: Boolean, default: true},
            linkable: {type: Boolean, default: true},
        },
        data() {
            return {expanded: this.open};
        },
        computed: {
            isOpen() {
                return this.page || this.expanded;
            },
            buttons() {
                return this.article.actions || [];
            }
        },
        watch: {
            open(v) {
                this.expanded = v;
            }
        },
        methods: {
            toggle() {
                if (this.page) return;
                this.expanded = !this.expanded;
                if (this.expanded) this.$emit('opened', this.article);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .help-article {
        border-bottom: 1px solid #e6eaef;

        &:last-child {
            border-bottom: none;
        }
    }

    .help-question {
        display: flex;
        align-items: flex-start;
        padding: 0.9rem 1.25rem;
        color: #224C74;
        font-weight: 600;
        line-height: 1.4;

        .toggle-icon {
            margin-top: 0.2rem;
            color: #9faec2;
            flex-shrink: 0;
        }
    }

    a.help-question:hover {
        background: #f7f9fb;
        color: #336699;
    }

    .is-open a.help-question {
        background: #f7f9fb;
    }

    .help-answer {
        padding: 0.25rem 1.25rem 1.25rem 3.1rem;
        background: #f7f9fb;

        @media (max-width: 768px) {
            padding-left: 1.25rem;
        }
    }

    .help-body {
        color: #4a4a4a;
        line-height: 1.6;
        font-size: 0.95rem;

        ::v-deep p:last-child, ::v-deep ul:last-child, ::v-deep ol:last-child {
            margin-bottom: 0;
        }

        ::v-deep li + li {
            margin-top: 0.35rem;
        }
    }

    .help-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 1rem;
    }

    .help-meta {
        margin-top: 0.85rem;
        font-size: 0.8rem;

        a {
            color: #8592a6;

            &:hover {
                color: #336699;
            }
        }
    }

    // Full article page: no row chrome, larger question
    .is-page {
        border-bottom: none;

        .help-question {
            font-size: 1.35rem;
            font-weight: bold;
            padding: 0 0 1rem;
            margin: 0;
            border-bottom: 2px solid #e0e2e8;
        }

        .help-answer {
            background: none;
            padding: 1.25rem 0 0;
        }

        .help-body {
            font-size: 1rem;
        }
    }
</style>
